import { OverlayPage } from '@client/components/_core/layout/OverlayPage';
import { PageController } from '@client/components/_core/layout/PageController';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { confirm } from '@client/components/_ui/feedback/confirm-modal';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import { modal } from '@client/components/_ui/feedback/modal';
import {
    SchemaEditorTab,
    SchemaGeneralTab,
    SchemaPreviewTab,
    useSchemaForm,
} from '@client/components/dynamic-form/SchemaEditor';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { trpc } from '@client/trpc/client';
import { error as logError } from '@client/utils/logger';
import type { DynamicFormSchema } from '@shared/schemas/dynamic-forms/dynamic-form-schema';
import { skipToken } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Space, Spin, Tabs, Tag } from 'antd';
import { Archive, FileCode, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useId, useState } from 'react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/compliance/attestations/user-attestations/edit/$id',
)({
    staticData: {
        presentation: 'overlay',
        metadata: {
            title: 'Edit form',
        },
    },
    params: {
        parse({ id }) {
            return {
                id: BigInt(id),
            };
        },
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <FormSchemaEdit />
        </PageController>
    );
}

function FormSchemaEdit() {
    const formId = useId();
    const { trpcUtils } = useTrpc();
    const { id } = Route.useParams();
    const { mutate: updateMutate } = trpc.formSchema.updateFormSchema.useMutation();
    const { mutate: publishMutate } = trpc.formSchema.publishFormSchema.useMutation();
    const { mutate: archiveMutate } = trpc.formSchema.archiveFormSchema.useMutation();
    const { mutate: deleteMutate } = trpc.formSchema.deleteFormSchema.useMutation();
    const navigate = useNavigate();
    const [form] = Form.useForm();

    const [activeTab, setActiveTab] = useState('general');

    const schemaQuery = trpc.formSchema.getFormSchema.useQuery(id ? { schemaId: id } : skipToken);
    const schemaData = schemaQuery.data;

    const {
        schemaJson,
        parsedSchema,
        parseError,
        copied,
        formStore,
        formatJson,
        copySchema,
        updateSchema,
        updateSchemaJson,
        setSchemaJson,
    } = useSchemaForm();

    // Initialize form when schema data loads
    useEffect(() => {
        if (schemaData) {
            const schema = schemaData.schema as DynamicFormSchema;
            setSchemaJson(JSON.stringify(schema, null, 2));
            form.setFieldsValue({
                formTitle: schemaData.formTitle,
                schemaKey: schemaData.schemaKey,
                tags: schemaData.tags || [],
                notes: schemaData.versionNotes || '',
            });
        }
    }, [schemaData, schemaData?.id, form, setSchemaJson]);

    const handleSchemaGenerated = useCallback(
        (schema: DynamicFormSchema) => {
            updateSchema(schema);

            if (schema.title && schema.title !== form.getFieldValue('formTitle')) {
                form.setFieldValue('formTitle', schema.title);
            }

            setActiveTab('preview');
        },
        [form, updateSchema],
    );

    const handleFormSubmit = useCallback(async () => {
        message.success('Form submitted successfully!');
    }, []);

    const handleReset = useCallback(() => {
        if (formStore) {
            formStore.getState().reset();
        }
    }, [formStore]);

    const handleArchive = (onClose: () => void) => {
        confirm({
            title: 'Archive form?',
            content: `Are you sure you want to archive "${schemaData?.formTitle}"?`,
            okText: 'Archive',
            onOk: () => {
                message.loading('Archiving...');
                archiveMutate(
                    { schemaId: id, reason: 'Archived by user' },
                    {
                        onSuccess() {
                            message.success('Form archived successfully');
                            onClose();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const handleDelete = (onClose: () => void) => {
        if (schemaData?.status !== 'DRAFT') {
            modal.warning({
                title: 'Cannot delete',
                content:
                    'Only draft forms can be deleted. Published or archived forms must be retained for audit purposes.',
            });
            return;
        }

        confirm({
            title: 'Delete form?',
            content: `Are you sure you want to delete "${schemaData?.formTitle}"? This action cannot be undone.`,
            okText: 'Delete',
            okDanger: true,
            onOk: () => {
                message.loading('Deleting...');
                deleteMutate(
                    { schemaId: id },
                    {
                        onSuccess() {
                            message.success('Form deleted successfully');
                            onClose();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const isDraft = schemaData?.status === 'DRAFT';
    const isPublished = schemaData?.status === 'PUBLISHED';
    const isArchived = schemaData?.status === 'ARCHIVED';

    const handleSaveDraft = async () => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            message.loading('Saving...');

            updateMutate(
                {
                    schemaId: id,
                    schema: schemaToSave,
                    tags: values.tags || [],
                    versionNotes: values.notes || undefined,
                },
                {
                    onSuccess() {
                        message.success('Form updated successfully');
                        void schemaQuery.refetch();
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        } catch (error) {
            logError(error);
        }
    };

    const handlePublishExisting = (onClose: () => void) => {
        confirm({
            title: 'Publish form?',
            content: 'Publishing will make this version active. Are you sure?',
            okText: 'Publish',
            onOk: () => {
                message.loading('Publishing...');
                publishMutate(
                    { schemaId: id },
                    {
                        onSuccess() {
                            message.success('Form published successfully');
                            onClose();
                        },
                        onError(error) {
                            errorMessage.show(error);
                        },
                    },
                );
            },
        });
    };

    const handleSaveNewVersion = async () => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            message.loading('Creating new version...');

            updateMutate(
                {
                    schemaId: id,
                    schema: schemaToSave,
                    tags: values.tags || [],
                    versionNotes: values.notes || undefined,
                },
                {
                    async onSuccess(newVersion) {
                        // Check if a new version was actually created
                        if (newVersion.id === id && newVersion.version === schemaData?.version) {
                            message.info('No changes detected - version not created');
                            return;
                        }

                        message.success(`New draft version v${newVersion.version} created successfully`);

                        await trpcUtils.formSchema.invalidate();

                        navigate({
                            to: '/console/$orgId/configuration/compliance/attestations/user-attestations/edit/$id',
                            params: { orgId: newVersion.orgId, id: newVersion.id },
                            replace: true,
                        });
                    },
                    onError(error) {
                        errorMessage.show(error);
                    },
                },
            );
        } catch (error) {
            logError(error);
        }
    };

    const handleUpdateAndPublish = async (onClose: () => void) => {
        try {
            const values = await form.validateFields();
            if (!parsedSchema) {
                message.error('Please fix schema errors before saving');
                return;
            }

            // Ensure the schema title matches the form title
            const schemaToSave = {
                ...parsedSchema,
                title: values.formTitle || parsedSchema.title,
            };

            confirm({
                title: 'Update and publish?',
                content: 'This will create a new version and immediately publish it as the active version.',
                okText: 'Update & Publish',
                onOk: () => {
                    message.loading('Updating and publishing...');

                    updateMutate(
                        {
                            schemaId: id,
                            schema: schemaToSave,
                            tags: values.tags || [],
                            versionNotes: values.notes || undefined,
                        },
                        {
                            onSuccess(newVersion) {
                                // After creating new version, publish it
                                publishMutate(
                                    { schemaId: newVersion.id },
                                    {
                                        onSuccess() {
                                            message.success('Form updated and published successfully');
                                            onClose();
                                        },
                                        onError(error) {
                                            errorMessage.show(error);
                                        },
                                    },
                                );
                            },
                            onError(error) {
                                errorMessage.show(error);
                            },
                        },
                    );
                },
            });
        } catch (error) {
            logError(error);
        }
    };

    return (
        <OverlayPage
            size="xLarge"
            title={
                <div className="flex items-center gap-3">
                    <span>{schemaData?.formTitle || 'Edit form'}</span>
                    {schemaData && (
                        <>
                            <span className="text-subtle-foreground">•</span>
                            <Tag variant="outlined" color="default" className="font-mono">
                                v{schemaData.version}
                            </Tag>
                            <span className="text-subtle-foreground">•</span>
                            <StatusBadge status={schemaData.status} module="formSchema" />
                        </>
                    )}
                </div>
            }
            afterClose={() => {
                form.resetFields();
                void trpcUtils.formSchema.invalidate();
                navigate({ moveInMatches: -1 });
            }}
            footer={({ onClose }) => (
                <div className="flex w-full items-center justify-between">
                    <Space>
                        {isDraft && (
                            <Button danger icon={<Trash2 size={16} />} onClick={() => handleDelete(onClose)}>
                                Delete
                            </Button>
                        )}
                    </Space>
                    <Space>
                        <Button onClick={onClose}>Close</Button>

                        {isDraft && (
                            <>
                                <Button
                                    type="secondary"
                                    onClick={() => handlePublishExisting(onClose)}
                                    disabled={!parsedSchema}
                                >
                                    Publish
                                </Button>
                                <Button type="primary" onClick={handleSaveDraft} disabled={!parsedSchema}>
                                    Save Draft
                                </Button>
                            </>
                        )}

                        {isPublished && (
                            <>
                                <Button
                                    type="secondary"
                                    icon={<Archive size={16} />}
                                    onClick={() => handleArchive(onClose)}
                                >
                                    Archive
                                </Button>
                                <Button type="secondary" onClick={handleSaveNewVersion} disabled={!parsedSchema}>
                                    Save New Version
                                </Button>
                                <Button
                                    type="primary"
                                    onClick={() => handleUpdateAndPublish(onClose)}
                                    disabled={!parsedSchema}
                                >
                                    Update & Publish
                                </Button>
                            </>
                        )}

                        {isArchived && (
                            <Button type="primary" onClick={handleSaveNewVersion} disabled={!parsedSchema}>
                                Save New Version
                            </Button>
                        )}
                    </Space>
                </div>
            )}
        >
            {() => (
                <div className="flex h-full flex-col">
                    {schemaQuery.isLoading ? (
                        <div className="flex h-full items-center justify-center">
                            <Spin size="large" />
                        </div>
                    ) : (
                        <Form id={formId} form={form} layout="vertical">
                            <Tabs
                                activeKey={activeTab}
                                onChange={setActiveTab}
                                items={[
                                    {
                                        key: 'general',
                                        label: 'Form settings',
                                        forceRender: true,
                                        children: (
                                            <SchemaGeneralTab
                                                mode="edit"
                                                schemaKey={schemaData?.schemaKey}
                                                version={schemaData?.version}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'editor',
                                        label: (
                                            <span className="flex items-center gap-2">
                                                <FileCode size={16} />
                                                Form editor
                                            </span>
                                        ),
                                        forceRender: true,
                                        children: (
                                            <SchemaEditorTab
                                                schemaJson={schemaJson}
                                                parseError={parseError}
                                                copied={copied}
                                                existingSchema={parsedSchema}
                                                onSchemaChange={updateSchemaJson}
                                                onFormatJson={formatJson}
                                                onCopySchema={copySchema}
                                                onSchemaGenerated={handleSchemaGenerated}
                                            />
                                        ),
                                    },
                                    {
                                        key: 'preview',
                                        label: 'Form preview',
                                        children: (
                                            <SchemaPreviewTab
                                                parsedSchema={parsedSchema}
                                                formStore={formStore}
                                                onReset={handleReset}
                                                onSubmit={handleFormSubmit}
                                            />
                                        ),
                                    },
                                ]}
                            />
                        </Form>
                    )}
                </div>
            )}
        </OverlayPage>
    );
}
